import React, { useContext } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ErrorCard from '../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import GenericCard from '../../../../../shared/components/v3/Cards/GenericCard/GenericCard';
import GenericCarousel from '../../../../../shared/components/v3/GenericCarousel/GenericCarousel';
import { BENEFIT_DETAIL_ROUTE, INSURANCE_DETAIL_ROUTE } from '../../../../../shared/constants/auth';
import {
  BENEFITS_SOURCE_SUPPLEMENTARY_TYPES,
  FLEXIBLE_REMUNERATION_SOURCE_TYPE,
  HEALTH_INSURANCE_SUB_SOURCE_TYPE,
  INSURANCE_SOURCE_TYPE
} from '../../../../../shared/constants/benefits';
import { NORTH_EAST_ARROW } from '../../../../../shared/constants/images';
import { UPGRADE_PROTECTION } from '../../../../../shared/constants/plan';
import {
  CLICK_ACTION,
  MY_PLAN_HEALTH_BENEFIT_DETAIL,
  SCREEN_MY_PLANS
} from '../../../../../shared/constants/tagManager';
import { PlansContext } from '../../../../../shared/context/plans/plansContext';
import { ICompanyBenefit, MyPlanSectionProps } from '../../../../../shared/interfaces/plans';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATION_MY_PLAN } from '../../../../../shared/translations/plan';

const HealthCard = ({ benefitsState, modalHandler, refetch }: MyPlanSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { assingSelectedBenefit } = useContext(PlansContext);

  const handleClickHealthBenefit = (benefit: ICompanyBenefit) => {
    assingSelectedBenefit(benefit);
    const gtmParams = {
      ...SCREEN_MY_PLANS,
      benefit_sku: benefit.sku,
      benefit_id: benefit.benefitId,
      provider: benefit.providerName
    };
    TagManagerService.pushEvent(CLICK_ACTION, MY_PLAN_HEALTH_BENEFIT_DETAIL, gtmParams);
    const isSuplementaryType = BENEFITS_SOURCE_SUPPLEMENTARY_TYPES.includes(
      benefit.subSourceType as (typeof BENEFITS_SOURCE_SUPPLEMENTARY_TYPES)[number]
    );
    if (
      benefit.sourceType === INSURANCE_SOURCE_TYPE ||
      isSuplementaryType ||
      (benefit.sourceType === FLEXIBLE_REMUNERATION_SOURCE_TYPE &&
        benefit.subSourceType === HEALTH_INSURANCE_SUB_SOURCE_TYPE)
    ) {
      return navigate(INSURANCE_DETAIL_ROUTE);
    }
    navigate(BENEFIT_DETAIL_ROUTE);
  };

  const healthElements = () => {
    if (benefitsState.data.benefits.health) {
      return benefitsState.data.benefits.health.map((benefit) => (
        <GenericCard
          key={benefit.benefitId}
          title={benefit.title}
          description={benefit.providerName}
          size="m"
          icon={benefit.icon}
          iconButton={NORTH_EAST_ARROW}
          action={() => handleClickHealthBenefit(benefit)}
        />
      ));
    }
    return [];
  };

  if (benefitsState.error) {
    return (
      <div className="section-error">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={refetch}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          className="flex justify-center"
        />
      </div>
    );
  }

  if (benefitsState.isLoading) {
    return <Skeleton variant="rectangular" className="section-skeleton" />;
  }

  if (!benefitsState.data.benefits.health) {
    return null;
  }

  return (
    <div className="carousel-container flex flex-col justify-center items-center">
      <GenericCarousel
        sliders={healthElements()}
        topData={{
          title: t(TRANSLATION_MY_PLAN.cards.protection.title),
          description: t(TRANSLATION_MY_PLAN.cards.protection.description)
        }}
        buttonData={{
          text: t(TRANSLATION_MY_PLAN.cards.protection.button),
          action: () => modalHandler(UPGRADE_PROTECTION)
        }}
      />
    </div>
  );
};

export default HealthCard;
